import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Package,
  Truck,
  Link2,
  CreditCard,
  DollarSign,
  CheckCircle2,
  ClipboardCheck,
  Send,
  Wallet,
  ShieldCheck,
} from 'lucide-react';
import { AffiliateHeader, AffiliateFooter } from '../../components/affiliate/AffiliateChrome';
import { buildAffiliatePath } from '../../lib/affiliate';

const BENEFITS = [
  { icon: Package,    title: 'No inventory',          desc: 'We handle stock, packing, and fulfillment for every order you generate.' },
  { icon: Truck,      title: 'No logistics',          desc: 'Forget shipping, returns, and customer service — our team takes care of it.' },
  { icon: Link2,      title: 'Custom affiliate link', desc: 'Get your own subdomain like johndoe.axonmuscle.com to share with your audience.' },
  { icon: CreditCard, title: 'Unique billing descriptor', desc: 'Sales appear on customer statements with a descriptor unique to your account for clean attribution. Example: AXON*AFFILIATE_NAME' },
  { icon: DollarSign, title: 'Earnings per sale',     desc: 'Earn a competitive commission on every order driven through your link.' },
  { icon: ShieldCheck,title: 'Trusted brand',         desc: 'Promote products backed by a premium fitness brand with proven conversion.' },
];

const STEPS = [
  { icon: Send,           title: 'Apply',                 desc: 'Fill out a short application with your details and traffic source.' },
  { icon: ClipboardCheck, title: 'Get approved',          desc: 'Our team reviews your application — most are answered within 48 hours.' },
  { icon: Link2,          title: 'Receive your link',     desc: 'You’ll get a unique subdomain (yourname.axonmuscle.com) and dashboard access.' },
  { icon: ArrowRight,     title: 'Start selling',         desc: 'Share your link anywhere — social, email, blog, video. Every visitor is tracked.' },
  { icon: Wallet,         title: 'Get paid',              desc: 'Earn commissions on every sale. Payouts via your chosen method.' },
];

const AffiliateLandingPage = () => {
  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <AffiliateHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-950 via-neutral-950 to-axon-950" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div
          className="absolute -top-40 -right-32 w-[640px] h-[640px] rounded-full opacity-30 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.55), transparent 60%)' }}
        />
        <div
          className="absolute -bottom-32 -left-24 w-[520px] h-[520px] rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.4), transparent 60%)' }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-axon-500/10 border border-axon-500/30 text-axon-400 text-xs font-semibold uppercase tracking-widest mb-6">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Affiliate Program
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Earn with <span className="text-axon-500">Axon Muscle</span> —
              <br className="hidden sm:inline" /> Launch Your Own Fitness Store
              <br className="hidden sm:inline" /> in Minutes.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-neutral-300 max-w-2xl">
              Sell our premium supplements through your own link and earn commissions on every order. No inventory, no logistics — just your audience and your earnings.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to={buildAffiliatePath('/apply')}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider text-sm shadow-button hover:shadow-glow-cyan transition-all"
              >
                Apply Now
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to={buildAffiliatePath('/login')}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl border border-neutral-700 hover:border-axon-500 text-neutral-200 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                Affiliate Login
              </Link>
            </div>
            <p className="mt-5 text-xs text-neutral-500">
              By joining, you agree that transactions through your link may appear on customer statements with a billing descriptor unique to your affiliate account.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight">Why join Axon Muscle</h2>
            <p className="mt-3 text-neutral-400 max-w-2xl mx-auto">Built for creators, coaches, and gym communities. We handle the infrastructure — you bring the audience.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="group p-7 rounded-2xl bg-neutral-900/60 border border-neutral-800 hover:border-axon-600/50 transition-all">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-axon-600 to-axon-500 flex items-center justify-center mb-5 group-hover:shadow-glow-cyan transition-all">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">{title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-neutral-900/40 border-y border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight">How it works</h2>
            <p className="mt-3 text-neutral-400 max-w-2xl mx-auto">Five steps from application to your first commission.</p>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-5 gap-5">
            {STEPS.map((step, idx) => (
              <li key={step.title} className="relative p-6 rounded-2xl bg-neutral-950 border border-neutral-800">
                <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-gradient-to-br from-axon-500 to-axon-400 text-white text-sm font-bold flex items-center justify-center shadow-lg">
                  {idx + 1}
                </div>
                <step.icon className="w-7 h-7 text-axon-500 mb-3" />
                <h3 className="font-display text-lg font-bold mb-1">{step.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-axon-700 via-axon-600 to-axon-500 p-10 md:p-14 text-center">
            <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.4), transparent 60%)' }} />
            <div className="relative">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-3">Ready to start earning?</h2>
              <p className="text-white/90 max-w-2xl mx-auto mb-7">Join the Axon Muscle Affiliate Program and turn your audience into recurring revenue.</p>
              <Link
                to={buildAffiliatePath('/apply')}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-axon-700 hover:text-axon-800 font-bold uppercase tracking-wider text-sm shadow-2xl hover:shadow-white/30 transition-all"
              >
                Apply Now
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <AffiliateFooter />
    </div>
  );
};

export default AffiliateLandingPage;
